<script setup lang="ts">
import PdsIcon from '@/components/pds/PdsIcon.vue';
import PdsPagination from '@/components/pds/PdsPagination.vue';
import PdsSegmented from '@/components/pds/PdsSegmented.vue';
import { computed, onBeforeUnmount, onMounted, useTemplateRef } from 'vue';
import HistoryTable from '../components/HistoryTable.vue';
import PageBar from '../components/PageBar.vue';
import PageTitle from '../components/PageTitle.vue';
import type { HistoryStatus } from '../history';
import { usePdsHistoryStore } from '../stores/history.store';

const history = usePdsHistoryStore();
const field = useTemplateRef<HTMLInputElement>('field');

const searching = computed(() => history.query.q.trim() !== '');

// Le nombre n'est connu que pour la liste affichée : les autres onglets n'en portent pas.
const statuses = computed(() =>
  (
    [
      { value: 'all', label: 'Tout' },
      { value: 'handed', label: 'Remises' },
      { value: 'cancelled', label: 'Annulées' },
    ] satisfies { value: HistoryStatus; label: string }[]
  ).map((option) => ({
    ...option,
    label:
      option.value === history.query.status && history.status === 'ready'
        ? `${option.label} · ${history.total}`
        : option.label,
  })),
);

const subtitle = computed(() =>
  searching.value
    ? `${history.total} commande${history.total > 1 ? 's' : ''} pour « ${history.query.q.trim()} » · toutes journées confondues`
    : 'La recherche couvre toutes les journées',
);

// Une recherche démarre toujours à blanc : les filtres de l'historique ne la suivent pas ici.
onMounted(async () => {
  field.value?.focus();
  await history.resetFilters();
});
onBeforeUnmount(() => history.stopTimers());
</script>

<template>
  <PageBar tall>
    <PageTitle
      :title="searching ? 'Résultats' : 'Recherche'"
      :subtitle="subtitle"
    />
  </PageBar>

  <main
    class="no-scrollbar flex min-h-0 flex-1 flex-col gap-[14px] overflow-auto px-4 py-5 sm:px-5"
  >
    <div class="flex flex-wrap items-center gap-3">
      <PdsSegmented
        :model-value="history.query.status"
        :options="statuses"
        @update:model-value="history.setFilters({ status: $event })"
      />
      <label
        class="bg-carte border-filet-fort text-encre-douce focus-within:text-encre flex h-12 min-w-[240px] flex-1 items-center gap-[10px] rounded-full border px-[14px]"
      >
        <PdsIcon name="search" :size="18" :stroke="1.7" />
        <input
          ref="field"
          type="search"
          placeholder="Rechercher un numéro, un nom, un téléphone"
          aria-label="Rechercher une commande"
          class="text-encre placeholder:text-encre-douce min-w-0 flex-1 bg-transparent text-[14px] leading-[20px] outline-none"
          :class="searching && 'font-semibold'"
          :value="history.query.q"
          @input="history.setSearch(($event.target as HTMLInputElement).value)"
        />
      </label>
    </div>

    <p
      v-if="history.status === 'error'"
      role="alert"
      class="bg-alerte-doux text-alerte-texte flex items-center justify-between gap-3 rounded-[16px] px-4 py-3 text-[14px]"
    >
      Impossible de charger les commandes.
      <button
        type="button"
        class="cursor-pointer font-semibold underline"
        @click="history.load()"
      >
        Réessayer
      </button>
    </p>
    <p
      v-else-if="history.status === 'ready' && !history.rows.length"
      class="text-encre-douce py-10 text-center text-[16px]"
    >
      Aucune commande ne correspond à cette recherche.
    </p>
    <div
      v-else
      :class="history.status === 'loading' ? 'opacity-60' : ''"
      :aria-busy="history.status === 'loading'"
    >
      <HistoryTable :rows="history.rows" />
    </div>

    <footer
      v-if="history.pageCount > 1"
      class="flex flex-wrap items-center justify-between gap-3"
    >
      <p class="text-encre-douce text-[14px]">
        {{ history.firstShown }}–{{ history.lastShown }} sur {{ history.total }}
      </p>
      <PdsPagination
        :page="history.query.page"
        :page-count="history.pageCount"
        @change="history.setPage($event)"
      />
    </footer>
  </main>
</template>
